import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: property, isLoading, error } = useQuery({
    queryKey: ['property', id],
    queryFn: () => api.properties.get(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading property details...</div>;
  }

  if (error || !property) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">
          {error ? (error as Error).message : 'Property not found'}
        </p>
        <Link to="/properties" className="btn btn-primary">
          Back to Properties
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link to="/properties" className="text-primary-600 hover:text-primary-700 text-sm">
        &larr; Back to Properties
      </Link>

      {/* Header */}
      <div className="card p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{property.title}</h1>
            <p className="text-gray-600 mt-1">
              {property.address && `${property.address}, `}
              {property.city}, {property.region && `${property.region}, `}
              {property.country}
            </p>
          </div>
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full ${
              property.availability_status === 'available'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {property.availability_status || 'Unknown'}
          </span>
        </div>

        {property.description && (
          <p className="mt-4 text-gray-700">{property.description}</p>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <h3 className="text-sm text-gray-500">Total Area</h3>
          <p className="text-xl font-bold text-gray-900">
            {property.total_area_sqm?.toLocaleString() || '-'} m²
          </p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm text-gray-500">Clear Height</h3>
          <p className="text-xl font-bold text-gray-900">{property.clear_height_m || '-'} m</p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm text-gray-500">Loading Docks</h3>
          <p className="text-xl font-bold text-gray-900">{property.num_docks || '-'}</p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm text-gray-500">Floor Load</h3>
          <p className="text-xl font-bold text-gray-900">
            {property.floor_load_kg_sqm ? `${property.floor_load_kg_sqm} kg/m²` : '-'}
          </p>
        </div>
      </div>

      {/* Pricing */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {property.rent_eur_sqm && (
            <div>
              <h3 className="text-sm text-gray-500">Monthly Rent</h3>
              <p className="text-2xl font-bold text-primary-600">
                {property.rent_eur_sqm.toFixed(2)} EUR/m²
              </p>
              {property.total_area_sqm && (
                <p className="text-sm text-gray-500 mt-1">
                  Total: {(property.rent_eur_sqm * property.total_area_sqm).toLocaleString()} EUR/month
                </p>
              )}
            </div>
          )}
          {property.sale_price_eur && (
            <div>
              <h3 className="text-sm text-gray-500">Sale Price</h3>
              <p className="text-2xl font-bold text-primary-600">
                {property.sale_price_eur.toLocaleString()} EUR
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Broker Info */}
      {property.broker_company && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact</h2>
          <div className="space-y-2">
            <p className="font-medium text-gray-900">{property.broker_name || property.broker_company}</p>
            {property.broker_company !== property.broker_name && (
              <p className="text-gray-600">{property.broker_company}</p>
            )}
            {property.broker_email && (
              <p className="text-gray-600">
                <a href={`mailto:${property.broker_email}`} className="text-primary-600 hover:underline">
                  {property.broker_email}
                </a>
              </p>
            )}
            {property.broker_phone && <p className="text-gray-600">{property.broker_phone}</p>}
          </div>
          {property.listing_url && (
            <a
              href={property.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary mt-4 inline-block"
            >
              View Original Listing
            </a>
          )}
        </div>
      )}

      {/* Price History */}
      {property.price_history && property.price_history.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Price History</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Rent (EUR/m²)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {property.price_history.map((entry, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(entry.recorded_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {entry.rent_eur_sqm?.toFixed(2) || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{entry.source || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Related Listings */}
      {property.related_listings && property.related_listings.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Other Sources ({property.related_listings.length})
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            This property is also listed on the following platforms:
          </p>
          <div className="space-y-2">
            {property.related_listings.map((listing) => (
              <div
                key={listing.id}
                className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm font-medium">{listing.broker_company || 'Unknown'}</span>
                {listing.rent_eur_sqm && (
                  <span className="text-sm text-gray-600">
                    {listing.rent_eur_sqm.toFixed(2)} EUR/m²
                  </span>
                )}
                {listing.listing_url && (
                  <a
                    href={listing.listing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline text-sm"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="card p-4 text-sm text-gray-500">
        <div className="flex justify-between">
          <span>First seen: {property.first_seen_at ? new Date(property.first_seen_at).toLocaleDateString() : '-'}</span>
          <span>Last updated: {property.last_seen_at ? new Date(property.last_seen_at).toLocaleDateString() : '-'}</span>
        </div>
      </div>
    </div>
  );
}
